import { h } from "preact";
import {
    useState,
    useRef,
    useLayoutEffect,
    useEffect,
    useMemo
} from "preact/hooks";

import { Color } from "../../../foundation/api/model";
import classname from "../../../foundation/utils/classname";
import { getColorName } from "../../../foundation/utils/dot";

import IntersectionObserverTarget from "../../../library/IntersectionObserverTarget";
import Button, { ButtonVariant } from "../../../library/v2/Button";

import IconArrowLeft from "../Icons/IconArrowLeft";
import IconArrowRight from "../Icons/IconArrowRight";
import OnlyIconButton from "../OnlyIconButton";

import styles from "./SlidingFilter.module.css";

interface FilterElement {
    label: string;
    color?: Color;
    badgeLabel?: string;
}

interface Props {
    selectedStatusFilter: string;
    containerWidth: number;
    filterTypes?: FilterElement[];
    onFilterChange: ({ statusFilter }: { statusFilter: string; }) => void;
    onFilterUpdate?: ({ statusFilter }: { statusFilter: string; }) => void;
    selectAllText?: string;
    appendSelectAllOptionToEnd?: boolean;
    class?: string;
}

function extendWithSelectedKeywordIfNecessary(
    keywords: FilterElement[],
    selectedKeyword?: string,
    selectAllText?: string
): FilterElement[] {
    const labels = keywords.map(keyword => keyword.label);
    if (!selectedKeyword || labels.includes(selectedKeyword) || selectedKeyword === selectAllText) {
        return [...new Set(keywords)];
    }

    return [...new Set([...keywords, { label: selectedKeyword }])];
}

function SlidingFilter({
    containerWidth,
    selectAllText = "All",
    selectedStatusFilter = "All",
    filterTypes,
    appendSelectAllOptionToEnd,
    onFilterChange,
    onFilterUpdate,
    class: cssClass
}: Props) {
    const filterSliderRef = useRef<HTMLDivElement>(null);
    const [overflowExists, setOverflowExists] = useState(false);
    const [hideChevron, setHideChevron] = useState({ left: true, right: true });

    // TODO-@emin remove this memo when Annotations related components are updated with transform props.
    const filterKeywords = useMemo(
        () => {
            if (appendSelectAllOptionToEnd) {
                return (
                    [
                        ...(filterTypes || []),
                        {
                            label: selectAllText
                        }
                    ]
                );
            }

            return ([
                {
                    label: selectAllText
                },
                ...(filterTypes || [])
            ]);
        },
        [JSON.stringify(filterTypes)]
    );

    const extendedKeywords = extendWithSelectedKeywordIfNecessary(
        filterKeywords,
        selectedStatusFilter
    );

    useLayoutEffect(() => {
        setTimeout(checkOverflow);
    }, [JSON.stringify(filterTypes)]);

    useLayoutEffect(checkOverflow, [containerWidth]);

    useEffect(checkOverflow, [selectedStatusFilter]);

    useEffect(() => {
        onFilterChange({ statusFilter: selectedStatusFilter });
        onFilterUpdate?.({ statusFilter: selectedStatusFilter });
    }, [selectedStatusFilter]);

    function scrollToSelected() {
        const selectedRef = filterSliderRef?.current?.querySelector(`[data-id=${selectedStatusFilter.replace(/\s/g, "")}]`) as HTMLElement | null;
        if (!selectedRef) {
            return;
        }

        const elementRight = selectedRef.offsetLeft + selectedRef.clientWidth;
        const elementLeft = selectedRef.offsetLeft;

        const containerRight = filterSliderRef?.current.scrollLeft + filterSliderRef?.current.clientWidth;
        const containerLeft = filterSliderRef?.current.scrollLeft;

        if (elementRight > containerRight) {
            filterSliderRef?.current?.scrollTo({ left: containerLeft + (elementRight - containerRight), behavior: "smooth" });
        } else if (elementLeft < containerLeft) {
            filterSliderRef?.current?.scrollTo({ left: containerLeft - (containerLeft - elementLeft), behavior: "smooth" });
        }
    }

    function checkOverflow() {
        const scrollWidth = filterSliderRef?.current?.scrollWidth ?? 0;
        const offsetWidth = filterSliderRef?.current?.offsetWidth ?? 0;

        setOverflowExists(scrollWidth > offsetWidth);
        setTimeout(scrollToSelected);
    }

    function createClickHandler(filterKeyword: string) {
        return () => {
            onFilterChange({ statusFilter: filterKeyword });
        };
    }

    function handleSlide(direction: number) {
        return () => {
            const slideAmount = (filterSliderRef?.current?.scrollWidth ?? 0) / 2;
            const currentPos = filterSliderRef?.current?.scrollLeft ?? 0;
            const newPos = currentPos + direction * slideAmount;

            filterSliderRef?.current?.scrollTo({ left: newPos, behavior: "smooth" });
        };
    }

    function handleOnEnter(_entry: unknown, item = "left") {
        setHideChevron({ ...hideChevron, [item]: true });
    }

    function handleOnLeave(_entry: unknown, item = "left") {
        setHideChevron({ ...hideChevron, [item]: false });
    }

    function renderFilterKeywords(
        filterKeyword: {
            label: string;
            color?: Color;
            badgeLabel?: string;
        },
        idx: number
    ) {
        const typeColor = filterKeyword.color ? getColorName(filterKeyword.color) : "gandalf";
        const filterButtonClass = classname(
            styles[typeColor],
            styles.filterButton,
            { [styles.selectedFilter]: selectedStatusFilter === filterKeyword.label },
            { [styles.selectedAllFilter]: filterKeyword.label === selectAllText }
        );

        let label: JSX.Element | string;

        if (filterKeyword.badgeLabel) {
            label = (
                <div
                    class={styles.labelWithBadge}>
                    {filterKeyword.label}<span class={classname(styles.badge, "caption-2", "semibold")}>{filterKeyword.badgeLabel}</span>
                </div>
            );
        } else {
            ({ label } = filterKeyword);
        }

        return (
            <Button
                key={filterKeyword}
                variant={ButtonVariant.Ninja}
                class={filterButtonClass}
                textClass="caption-1"
                data-testid={filterKeyword.label.replace(/\s/g, "")}
                data-id={filterKeyword.label.replace(/\s/g, "")}
                onClick={createClickHandler(filterKeyword.label)}>
                {overflowExists && (idx === 0 || idx === extendedKeywords.length - 1) ? (
                    <IntersectionObserverTarget
                        targetClass={styles.filterKeyword}
                        options={{ root: filterSliderRef?.current, threshold: 1 }}
                        observedItem={idx === 0 ? "left" : "right"}
                        onEnter={handleOnEnter}
                        onLeave={handleOnLeave}>
                        {label}
                    </IntersectionObserverTarget>
                ) : (
                    label
                )}
            </Button>
        );
    }

    return (
        <div class={classname(cssClass, styles.slidingFilterContainer, { [styles.overflowExists]: overflowExists })}>
            <OnlyIconButton
                icon={IconArrowLeft}
                class={classname(styles.sliderChevron, styles.chevronRight, { [styles.hideChevron]: hideChevron.left })}
                onClick={handleSlide(-1)}/>
            <div ref={filterSliderRef} class={styles.filterSlider}>
                {extendedKeywords.map(renderFilterKeywords)}
            </div>
            <OnlyIconButton
                icon={IconArrowRight}
                class={classname(styles.sliderChevron, styles.chevronLeft, { [styles.hideChevron]: hideChevron.right })}
                onClick={handleSlide(1)}/>
        </div>
    );
}

export default SlidingFilter;
